import React, { Fragment, FunctionComponent, useEffect, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import Select from 'react-select';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import * as yup from 'yup';
import TagManager from 'react-gtm-module';
import { Reorder } from 'framer-motion';
import PhoneInput from 'react-phone-input-2';

import Text from 'common/components/Text/Text';
import { SessionState, setFilters, setProfile, setUser } from 'features/session/sessionSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { CommonHash } from 'app/types';
import { profileAnswers, shuffleArray, updateUserAnswers } from 'common/utils';
import { Item } from './Item';
import RobotLoader from 'common/components/RobotLoader/RobotLoader';
import {
  DEGREE_TYPES_PROFILING,
  WORKING_AREAS,
  ENGLISH_TESTS,
  TUITION_RANGES,
  GPASCALE,
} from 'common/constants';
import { setLastStepDone, setPreferencesFilled } from 'features/user/userSlice';

import styles from './Profile.module.scss';
import { ReactComponent as RobotIcon } from 'assets/profile/robot.svg';
import { ReactComponent as RobotShadowIcon } from 'assets/profile/shadow_robot.svg';
import {
  useGetAllCountriesQuery,
  useGetAllSubjectsQuery,
  useAddProfileMutation,
  useGetProfileQuery,
} from 'services/exploreApi';
import { ReactComponent as CloseIcon } from 'assets/profile/close.svg';
import 'react-phone-input-2/lib/style.css';

const preferencesQuestions = 7;

const tuitionOptions = TUITION_RANGES.map(([k, min, max]) => ({
  label: k,
  value: [k, [min, max]],
}));

const intakeDateOptions = [
  { label: '2024', value: '2024' },
  { label: '2025', value: '2025' },
  { label: '2026 or later', value: '2026 or later' },
];

// const degreeTypeOptions = DEGREE_TYPES.map(([label, value]) => ({
//   label: label,
//   value: value,
// }));

const degreeTypeOptions = DEGREE_TYPES_PROFILING.map(([label, value]) => ({
  label: label,
  value: value,
}));

const testTypeOptions = ENGLISH_TESTS.map(([label, value]) => ({
  label: label,
  value: value,
}));

const workingAreaOptions = WORKING_AREAS.map(([label, value]) => ({
  label: label,
  value: value,
}));

const preferencesAnswers = [
  'countries',
  'subjects',
  'budget',
  'degree_type',
  'duration',
  'attendance',
  'mode',
  'preferred_intake_date',
];

const Profile: FunctionComponent = () => {
  const { profile, filters, logged, user } = useAppSelector(
    ({ session }: { session: SessionState }) => session
  );
  const location = useLocation();
  const searchUrlParams = new URLSearchParams(location.search);
  const utmSource = searchUrlParams.get('utm_source') || undefined;
  const utmMedium = searchUrlParams.get('utm_medium') || undefined;
  const utmContent = searchUrlParams.get('utm_content') || undefined;
  const utmCampaign = searchUrlParams.get('utm_campaign') || undefined;
  const utmTerm = searchUrlParams.get('utm_term') || undefined;
  const utmKeywords = searchUrlParams.get('utm_keywords') || undefined;

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [addProfile, { data: newProfile, error: newProfileError }] = useAddProfileMutation();
  const [existingEmail, setExistingEmail] = useState<string | undefined>(undefined);
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [currentPreferenceQuestion, setCurrentPreferenceQuestion] = useState<number>(0);
  const [preferenceQuestionsAnswered, setPreferenceQuestionsAnswered] = useState<number>(0);
  const [askEntireQuestionary, setAskEntireQuestionary] = useState<boolean>(true);
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
  const [isInvalidEmail, setIsInvalidEmail] = useState<boolean>(false);
  const [isInvalidName, setIsInvalidName] = useState<boolean>(false);
  const [isInvalidPhone, setIsInvalidPhone] = useState<boolean>(false);
  const [askEmail, setAskEmail] = useState<boolean>(false);
  const [skipQuestion, setSkipQuestion] = useState<boolean>(true);
  const [warningAdvice, setWarningAdvice] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [infoEventSent, setInfoEventSent] = useState<boolean>(false);
  const { data: countries } = useGetAllCountriesQuery();
  const { data: subjects } = useGetAllSubjectsQuery();
  const [answers, setAnswer] = useState<CommonHash>({});
  const [answersPercentage, setAnswersPercentage] = useState<number>(0);
  const workingAreaValues = workingAreaOptions
    .map((o: any) => o.value)
    .filter((v: any) => v !== 'other');
  const {
    data: loadedProfile,
    isLoading: isProfileLoading,
    isError,
  } = useGetProfileQuery(existingEmail || '', {
    skip: !existingEmail,
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const initialOptions = ['Employability', 'Cost', 'Subject', 'Location'];
  const [prioritiesOptions, setPriotitiesOptions] = useState(
    profile && profile.priorities?.length ? profile.priorities : shuffleArray(initialOptions)
  );

  const emailSchema = yup.object().shape({
    email: yup.string().email().required(),
  });

  const profileSchema = yup.object().shape({
    full_name: yup.string().required(),
    phone_number: yup
      .string()
      .required('Please enter a valid number')
      .min(11, 'Please enter a valid number'),
  });

  const onAnswerPush = (key: string) => (value: any) => {
    if (!value) return;
    setAnswer((a: any) => ({ ...a, [key]: [...new Set([...(a[key] || []), value])] }));
  };
  const onAnswerPop = (key: string) => (value: any) => {
    if (!value) return;
    setAnswer((a: any) => ({
      ...a,
      [key]: (a[key] || []).filter((v: any) => v !== value),
    }));
  };

  const setInputOption = (key: string, value: any) => {
    setAnswer((a: any) => ({ ...a, [key]: key === 'phone_number' ? `+${value}` : value }));
  };

  const typeProgramChange = (option: string, value: any) => {
    setAnswer((a: any) => ({ ...a, [option]: value }));
  };

  const isAnswersFull = (type: string) => {
    return answers[type]?.length == 3;
  };

  const validateProfile = async () => {
    const profileData = {
      full_name: answers.full_name,
      phone_number: answers.phone_number,
    };
    const emailData = {
      email: answers.email,
    };
    const isEmailValid = await emailSchema.isValid(emailData);
    const isProfileValid = await profileSchema.isValid(profileData);
    if (isEmailValid == true && isProfileValid == true) {
      createProfile();
    } else {
      if (isEmailValid != true) {
        setIsInvalidEmail(true);
        setWarningAdvice('Please enter a valid email');
      }
      if (isProfileValid != true) {
        answers.full_name ? setIsInvalidName(false) : setIsInvalidName(true);
        if (answers.phone_number && answers.phone_number.length < 12) {
          setIsInvalidPhone(true);
        } else if (answers.phone_number === undefined) {
          setIsInvalidPhone(true);
        } else {
          setIsInvalidPhone(false);
        }
      }
    }
  };

  const sendGTMEvents = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: profile ? 'profiling_edit_see_results_basic' : 'profiling_see_results_basic',
      },
    });
    TagManager.dataLayer({
      dataLayer: { event: 'user_identified', userId: profile?.id },
    });
  };
  const sendGTMEventsQuestions = (number: number) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: `profiling_q${number}`,
      },
    });
  };

  const sendGTMEActions = (number: number, skip: boolean) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: skip ? `profiling_q${number}_skip` : `profiling_q${number}_next`,
      },
    });
  };

  const cleanAnswers = () => {
    preferencesAnswers.map((key) => {
      if (key.includes('budget')) {
        if (answers['budget'] == null || answers['budget'][0] == 'Any') {
          answers['budget'] = answers['budget_min'] = answers['budget_max'] = null;
        } else {
          answers['budget_min'] = answers['budget'][1][0];
          answers['budget_max'] = answers['budget'][1][1];
          answers['budget'] = answers['budget'][0];
        }
      } else if (['subjects', 'countries'].includes(key)) {
        answers[key] ? null : (answers[key] = []);
      } else if (key == 'plan_take_exam') {
        answers.plan_take_exam !== undefined ? (answers.score_technical_exam = null) : null;
      } else if (key == 'score_technical_exam') {
        answers.score_technical_exam ? (answers.plan_take_exam = null) : null;
      } else {
        answers[key] ? null : (answers[key] = null);
      }
    });
  };

  const createProfile = () => {
    setIsLoading(true);
    cleanAnswers();
    updateFilters();
    if (logged) checkPreferencesFilled();
    if (answers.budget && answers.budget !== '<5k') {
      TagManager.dataLayer({
        dataLayer: {
          event: 'profiling_over_5k',
        },
      });
    }
    profile
      ? addProfile(answers)
      : addProfile({
          ...answers,
          utm_medium: utmMedium,
          utm_source: utmSource,
          utm_term: utmTerm,
          utm_campaign: utmCampaign,
          utm_content: utmContent,
          utm_keywords: utmKeywords,
        });
  };

  const cleanQuestions = () => {
    setAskEmail(false);
    setAnswer({});
    setIsInvalidEmail(false);
    setAcceptTerms(false);
  };

  const updateAnswersPercentage = () => {
    setAnswersPercentage(
      Math.round((100 * preferenceQuestionsAnswered) / preferencesAnswers.length)
    );
  };

  const onPrevQuestion = () => {
    if (currentQuestion) {
      setCurrentQuestion(
        (currentQuestion -
          (questions[currentQuestion - 1].skip || (logged && currentQuestion == 7) ? 2 : 1)) %
          questions.length
      );
      currentQuestion - 1 <= preferencesQuestions &&
        setCurrentPreferenceQuestion(currentPreferenceQuestion - 1);
    } else {
      navigate(-1);
    }
  };

  const onNextQuestion = (skip: boolean) => {
    const value =
      (currentQuestion + (questions[currentQuestion + 1].skip ? 2 : 1)) % questions.length;
    setCurrentQuestion(value);
    setTimeout(() => {
      TagManager.dataLayer({ dataLayer: { event: 'pageview_change' } });
    }, 1000);
    sendGTMEventsQuestions(value + 1);
    sendGTMEActions(value, skip);
    currentQuestion + 1 <= preferencesQuestions &&
      setCurrentPreferenceQuestion(currentPreferenceQuestion + 1);
  };

  const currentProgress = Math.round((100 * currentPreferenceQuestion) / preferencesQuestions);

  const updatePreferencesAnswered = () => {
    answers.budget && answers.budget[0] == 'Any' ? delete answers.budget : null;
    const questionsAnswered = preferencesAnswers.reduce(
      (prev, current) => (prev += Object.keys(answers).includes(current) ? 1 : 0),
      0
    );
    setPreferenceQuestionsAnswered(questionsAnswered);
  };

  const updateFilters = () => {
    const newFilters: CommonHash = new Object();
    Object.keys(filters).map((k: string) => {
      k.includes('order') ? null : (newFilters[k] = filters[k]);
    });
    newFilters['order_by_scoring'] = 'desc';
    dispatch(setFilters(newFilters));
  };

  const checkPreferencesFilled = () => {
    let count = 0;
    Object.keys(answers).forEach((key) => {
      if (preferencesAnswers.includes(key)) {
        if (['countries', 'subjects'].includes(key)) {
          if (answers[key].length) count++;
        } else {
          count++;
        }
      }
    });
    if (count / 6 === 1) {
      dispatch(setPreferencesFilled(true));
      dispatch(setLastStepDone(2));
    } else {
      dispatch(setPreferencesFilled(false));
    }
  };

  useEffect(() => {
    document.title = 'Unify | Profiling';
    setAskEmail(Boolean(searchParams.get('email')));
    if (logged) {
      setInputOption('email', user?.email);
      setInputOption('full_name', user?.name);
      setInputOption('phone_number', user?.phone);
    }
    setTimeout(() => {
      TagManager.dataLayer({
        dataLayer: { event: 'ga_event', eventName: profile ? 'profiling_edit' : 'profiling_start' },
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (askEmail) {
      answers.email = undefined;
    }
  }, [askEmail]);

  useEffect(() => {
    if (isError) {
      setIsInvalidEmail(true);
      setWarningAdvice('Email not found, please answer again the questions');
    } else {
      if (!isProfileLoading && loadedProfile?.data) {
        setAnswer(profileAnswers(loadedProfile.data));
        dispatch(setProfile(loadedProfile.data));
        onNextQuestion(false);
      }
    }
  }, [isProfileLoading]);

  useEffect(() => {
    if (newProfile) {
      dispatch(setProfile(newProfile.data));
      if (user) {
        const answersUserUpdtaed = updateUserAnswers(user, newProfile.data);
        if (answersUserUpdtaed) dispatch(setUser(answersUserUpdtaed));
      }
      sendGTMEvents();
      navigate(`/explore/${newProfile.data.degree_type || 'masters'}`);
    }
    if (newProfileError) {
      navigate(`/explore/masters`);
      Sentry.captureException(newProfileError);
    }
  }, [newProfile, newProfileError]);

  useMemo(() => {
    if (profile) {
      setAnswer(profileAnswers(profile));
      setAcceptTerms(true);
      profile.score_english_exam && setSkipQuestion(false);
    }
  }, [profile]);

  useEffect(() => {
    if (currentQuestion == 7 && !infoEventSent) {
      TagManager.dataLayer({
        dataLayer: { event: 'ga_event', eventName: 'profiling_lead_info_show' },
      });
      setInfoEventSent(true);
    }
  }, [currentQuestion]);

  useEffect(() => {
    Object.keys(answers).map((key) =>
      answers[key] == null || answers[key]?.length == 0 ? delete answers[key] : null
    );
    console.log(Object.keys(answers));
    updatePreferencesAnswered();
  }, [answers]);

  useEffect(() => {
    setAnswer((a: any) => ({ ...a, priorities: prioritiesOptions }));
  }, [prioritiesOptions]);

  useEffect(() => {
    updateAnswersPercentage();
  }, [preferenceQuestionsAnswered]);

  const workingAreaLabel = () => {
    const value = workingAreaOptions.find((o: any) => o.value === answers['working_area']);
    if (value) return value;
    return answers['working_area'] !== undefined ? { label: 'Other', value: '' } : null;
  };

  const degreeTypeLabel = () => {
    const value = degreeTypeOptions.find((o: any) => o.value === answers['degree_type']);
    if (value) return value;
  };

  const questions = useMemo(
    () => [
      {
        title: 'Where would you like to study?',
        comment: '(select up to three countires)',
        content: (
          <div className={styles.questionContent}>
            <Select
              isSearchable
              classNamePrefix="react-select"
              className={styles.questionSelect}
              placeholder="Search country"
              value={answers['countries']?.[answers.length - 1] || 'Search country'}
              options={countries?.data.map((c) => ({ label: c.name, value: c.id })) || []}
              onChange={({ value }: any) => onAnswerPush('countries')(value)}
              isDisabled={isAnswersFull('countries')}
            />
            <div className={`${styles.questionAnswer} ${styles.questionAnswerTag}`}>
              {(answers['countries'] || []).map((cId: number) => (
                <div
                  className={styles.answerTag}
                  key={cId}
                  onClick={() => onAnswerPop('countries')(cId)}
                >
                  {countries?.data.find((c) => c.id === cId)?.name}
                  <i className="bi bi-x"></i>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        title: 'Which subjects would you like to study?',
        comment: '(select up to three subjects)',
        content: (
          <div className={styles.questionContent}>
            <Select
              isSearchable
              classNamePrefix="react-select"
              className={styles.questionSelect}
              placeholder="Search subject"
              value={answers['subjects']?.[answers.length - 1] || 'Search subject'}
              options={subjects?.data.map((s) => ({ label: s.name, value: s.id })) || []}
              onChange={({ value }: any) => onAnswerPush('subjects')(value)}
              isDisabled={isAnswersFull('subjects')}
            />
            <div className={`${styles.questionAnswer} ${styles.questionAnswerTag}`}>
              {(answers['subjects'] || []).map((sId: number) => (
                <div
                  className={styles.answerTag}
                  key={sId}
                  onClick={() => onAnswerPop('subjects')(sId)}
                >
                  {subjects?.data.find((s) => s.id === sId)?.name}
                  <i className="bi bi-x"></i>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        title: "What's your tuition fee budget?",
        comment: '(in USD)',
        content: (
          <div className={styles.questionContent}>
            <Select
              isSearchable
              classNamePrefix="react-select"
              className={styles.questionSelect}
              placeholder="Choose a budget range"
              value={tuitionOptions.find((o: any) => o.label === (answers['budget']?.[0] || 'Any'))}
              options={tuitionOptions}
              onChange={({ value }: any) => setInputOption('budget', value)}
            />
          </div>
        ),
      },
      {
        title: 'What degree type are you looking for?',
        content: (
          <div className={styles.questionContent} key="degree_type">
            <Select
              isSearchable
              classNamePrefix="react-select"
              className={styles.questionSelect}
              placeholder="Choose degree type"
              value={degreeTypeLabel()}
              options={degreeTypeOptions}
              onChange={({ value }: any) => setInputOption('degree_type', value)}
            />
          </div>
        ),
      },
      {
        title: 'Which of these criteria are your priorities?',
        comment: '(long press and rank the priorities)',
        content: (
          <div className={styles.questionContent}>
            <Reorder.Group
              axis="y"
              onReorder={setPriotitiesOptions}
              values={prioritiesOptions}
              className={styles.optionsGroup}
            >
              {prioritiesOptions.map((item, index) => (
                <Item key={item} item={[item, index + 1]} />
              ))}
            </Reorder.Group>
          </div>
        ),
      },
      {
        title: (
          <div>
            <>What types of program are you interested in?</>
            {isMobile && (
              <Text
                size={14}
                color="secondary"
                weight={400}
                inlineStyle={{ marginLeft: '5px' }}
                inline
              >
                (multiple select)
              </Text>
            )}
          </div>
        ),
        comment: `${!isMobile ? '(multiple select)' : ''}`,
        content: (
          <>
            <div className={styles.typeProgramContainer}>
              <label>
                <i className="bi bi-clock"></i>Duration
              </label>
              <div className={styles.typeProgramButtons}>
                {[
                  ['Any', undefined],
                  ['12 months', 12],
                  ['18 months', 18],
                  ['24 months', 24],
                ].map(([k, v]) => (
                  <Fragment key={k}>
                    <input
                      type="radio"
                      className="btn-check"
                      name="duration"
                      id={`duration-${v}`}
                      checked={answers['duration'] === v}
                      onChange={() => typeProgramChange('duration', v)}
                    />
                    <label className="btn btn-primary" htmlFor={`duration-${v}`}>
                      {k}
                    </label>
                  </Fragment>
                ))}
              </div>
            </div>
            <div className={styles.typeProgramContainer}>
              <label>
                <i className="bi bi-calendar4-week"></i>Attendance
              </label>
              <div className={styles.typeProgramButtons}>
                {[
                  ['Any', undefined],
                  ['Full-time', 'full_time'],
                  ['Part-time', 'part_time'],
                ].map(([k, v]) => (
                  <Fragment key={k}>
                    <input
                      type="radio"
                      className="btn-check"
                      name="attendance"
                      id={`attendance-${v}`}
                      checked={answers['attendance'] === v}
                      onChange={() => typeProgramChange('attendance', v)}
                    />
                    <label className="btn btn-primary" htmlFor={`attendance-${v}`}>
                      {k}
                    </label>
                  </Fragment>
                ))}
              </div>
            </div>
            <div className={styles.typeProgramContainer}>
              <label>
                <i className="bi bi-easel3"></i>Mode
              </label>
              <div className={styles.typeProgramButtons}>
                {[
                  ['Any', undefined],
                  ['In person', 'in_person'],
                  ['Online', 'online'],
                  ['Blended', 'blended'],
                ].map(([k, v]) => (
                  <Fragment key={k}>
                    <input
                      type="radio"
                      className="btn-check"
                      name="mode"
                      id={`mode-${v}`}
                      checked={answers['mode'] === v}
                      onChange={() => typeProgramChange('mode', v)}
                    />
                    <label className="btn btn-primary" htmlFor={`mode-${v}`}>
                      {k}
                    </label>
                  </Fragment>
                ))}
              </div>
            </div>
          </>
        ),
      },
      {
        title: 'Which year would you start your studies?',
        content: (
          <div className={styles.questionContent} key="preferred_intake_date">
            <Select
              isSearchable
              classNamePrefix="react-select"
              className={styles.questionSelect}
              placeholder="Choose intake date"
              value={intakeDateOptions.find(
                (o: any) => o.label === answers['preferred_intake_date']
              )}
              options={intakeDateOptions}
              onChange={({ value }: any) => setInputOption('preferred_intake_date', value)}
            />
          </div>
        ),
      },
      {
        noHeader: true,
        content: (
          <div>
            <div className={styles.profileHeader}>
              <div className={styles.headerControl}>
                <div className={styles.prevPageIcon} onClick={onPrevQuestion}>
                  <i className="bi bi-chevron-left"></i>
                </div>
              </div>
              <div className={styles.headerRobot} style={{ marginBottom: '15px' }}>
                <RobotIcon />
              </div>
              <div className={styles.headerContent}>
                <div className={styles.headerNotification}>Your are doing great!</div>
                <div className={styles.headerDescription}>
                  UnifAi successfully processed your preference rate.
                </div>
              </div>
            </div>
            <div className={styles.profileProgressBar}>
              <div className={styles.barProgress} style={{ width: '100%' }}></div>
            </div>
            <div className={styles.quetionsContainer}>
              <div className={styles.question}>
                <div className={styles.questionTitle}>
                  Please leave your email so we can save your answers so far
                </div>
                <div className={styles.questionContent}>
                  <div className={styles.emailForm}>
                    <div className={styles.inputsContainter}>
                      <div className={styles.inputGroup}>
                        <Text
                          size={isMobile ? 12 : 16}
                          weight={isMobile ? 600 : 500}
                          inlineStyle={{ marginBottom: '5px' }}
                          color="primary"
                        >
                          Email
                        </Text>
                        <input
                          className={`form-control ${isInvalidEmail && styles.warningInput}`}
                          type="email"
                          onChange={(e) => setInputOption('email', e.target.value)}
                          value={answers['email'] ? answers['email'] : ''}
                          placeholder="My email"
                        />
                        {isInvalidEmail && (
                          <label className={styles.warningLabel}>{warningAdvice}</label>
                        )}
                      </div>
                      <div className={styles.inputGroup}>
                        <Text
                          size={isMobile ? 12 : 16}
                          weight={isMobile ? 600 : 500}
                          inlineStyle={{ marginBottom: '5px' }}
                          color="primary"
                        >
                          Full name
                        </Text>
                        <input
                          className={`form-control ${isInvalidName && styles.warningInput}`}
                          type="text"
                          onChange={(e) => setInputOption('full_name', e.target.value)}
                          value={answers['full_name'] ? answers['full_name'] : ''}
                          placeholder="Name"
                        />
                        {isInvalidName && (
                          <label className={styles.warningLabel}>Please enter your full name</label>
                        )}
                      </div>
                      <div className={styles.inputGroup}>
                        <Text
                          size={isMobile ? 12 : 16}
                          weight={isMobile ? 600 : 500}
                          inlineStyle={{ marginBottom: '5px' }}
                          color="primary"
                        >
                          Phone number
                        </Text>
                        <PhoneInput
                          inputClass={`${isInvalidPhone && styles.warningInput}`}
                          country={'gb'}
                          countryCodeEditable={false}
                          value={answers['phone_number'] ? `+${answers['phone_number']}` : null}
                          onChange={(e) => setInputOption('phone_number', e)}
                        />
                        {isInvalidPhone && (
                          <label className={styles.warningLabel}>
                            Please enter a valid phone number
                          </label>
                        )}
                      </div>
                    </div>
                    <div className={`form-check mb-5 ${styles.formGroup}`}>
                      <input
                        id="agree_with_terms"
                        className="form-check-input"
                        type="checkbox"
                        name="flexRadioDefault"
                        checked={acceptTerms}
                        onChange={() => setAcceptTerms(!acceptTerms)}
                      />
                      <label
                        className={`form-check-label ${styles.formLabel}`}
                        htmlFor="agree_with_terms"
                      >
                        I agree with Unify Terms and conditions
                      </label>
                    </div>
                    <div className={styles.submitForm}>
                      <button
                        type="submit"
                        className={`${styles.submitBtn} ${
                          acceptTerms ? '' : styles.submitBtnDisabled
                        }`}
                        disabled={acceptTerms ? false : true}
                        onClick={() => validateProfile()}
                      >
                        Save answers
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ),
      },
      {
        skip: skipQuestion,
        title: 'Tell us more about the english test',
        comment: '',
        content: (
          <div className={styles.questionContent}>
            <div className={styles.inputsContainter}>
              <div className={styles.inputGroup}>
                <Text
                  size={isMobile ? 12 : 16}
                  weight={isMobile ? 600 : 500}
                  inlineStyle={{ marginBottom: '5px' }}
                  color="primary"
                >
                  Test type
                </Text>
                <Select
                  isSearchable
                  key="test_type"
                  classNamePrefix="react-select"
                  className={styles.questionSelect}
                  placeholder="Choose test type"
                  value={testTypeOptions.find(
                    (o: any) => o.value === (answers['english_exam'] || 'Choose test type')
                  )}
                  options={testTypeOptions}
                  onChange={({ value }: any) => setInputOption('english_exam', value)}
                />
              </div>
              <div className={styles.inputGroup}>
                <Text
                  size={isMobile ? 12 : 16}
                  weight={isMobile ? 600 : 500}
                  inlineStyle={{ marginBottom: '5px' }}
                  color="primary"
                >
                  Test score
                </Text>
                <input
                  className={`form-control`}
                  type="number"
                  onChange={(e) => setInputOption('score_english_exam', e.target.value)}
                  value={answers.score_english_exam ? answers.score_english_exam : ''}
                  placeholder="Score"
                />
              </div>
            </div>
          </div>
        ),
      },
      {
        title: 'Let us know your working experience',
        comment: `(full time or part time jobs, internship is not included)`,
        content: (
          <div className={styles.questionContent}>
            <div className={styles.inputsContainter}>
              <div className={styles.inputGroup}>
                <Text
                  size={isMobile ? 12 : 16}
                  weight={isMobile ? 600 : 500}
                  inlineStyle={{ marginBottom: '5px' }}
                  color="primary"
                >
                  Working area in the last role
                </Text>
                <Select
                  isSearchable
                  key="working_area"
                  classNamePrefix="react-select"
                  className={styles.questionSelect}
                  placeholder="Search working area"
                  value={workingAreaLabel()}
                  options={workingAreaOptions}
                  onChange={({ value }: any) => setInputOption('working_area', value)}
                />
                {!workingAreaValues.includes(answers['working_area']) &&
                  answers['working_area'] !== undefined && (
                    <div>
                      <Text
                        size={16}
                        weight={isMobile ? 500 : 600}
                        inlineStyle={{ marginTop: '40px', marginBottom: '12px' }}
                        color="primary"
                      >
                        Type Working area
                      </Text>
                      <input
                        className={`form-control`}
                        type="text"
                        onChange={(e) => {
                          setInputOption('working_area', e.target.value);
                        }}
                        value={answers['working_area'] === 'other' ? '' : answers['working_area']}
                        placeholder="Working area"
                      />
                    </div>
                  )}
              </div>
              <div className={styles.inputGroup}>
                <Text
                  size={isMobile ? 12 : 16}
                  weight={isMobile ? 600 : 500}
                  inlineStyle={{ marginBottom: '5px' }}
                  color="primary"
                >
                  Years of work
                </Text>
                <input
                  className={`form-control`}
                  type="number"
                  onChange={(e) => setInputOption('years_of_work', e.target.value)}
                  value={answers.years_of_work ? answers.years_of_work : ''}
                  placeholder="Years of experience"
                />
              </div>
            </div>
          </div>
        ),
        lastQuestion: true,
      },
      {
        noHeader: true,
        content: (
          <div className={styles.quetionsContainer}>
            <div className={styles.prevPageIcon} onClick={onPrevQuestion}>
              <i className="bi bi-chevron-left"></i>
            </div>
            <div className={styles.questionRobot}>
              <RobotShadowIcon />
            </div>
            <div className={`mt-4 ${styles.question}`}>
              <div className={styles.centerContainer}>
                <div className={styles.closeQuestionsContainer}>
                  <div>
                    {isMobile && (
                      <>
                        <Text size={16} weight={700} color="secondary">
                          Amazing!
                        </Text>
                      </>
                    )}
                    <Text size={16} weight={700} color="secondary">
                      {`${!isMobile ? 'Amazing!' : ''} Thank you for finishing questions.`}
                    </Text>
                    <Text
                      size={20}
                      weight={700}
                      color="primary"
                      inlineStyle={{ padding: '20px 0' }}
                    >
                      You can find...
                    </Text>
                    <div className={styles.resultsLabel}>
                      <i className="bi bi-check-circle"></i>
                      <p>
                        <b>Best match programs</b> from our recommendation
                      </p>
                    </div>
                    <div className={styles.resultsLabel}>
                      <i className="bi bi-check-circle"></i>
                      <p>
                        <b>Perference rate and admission chance</b> for each program
                      </p>
                    </div>
                  </div>
                  <div className={styles.centerContainer}>
                    <button
                      className={styles.questionsNext}
                      style={{ width: '100%' }}
                      onClick={createProfile}
                    >
                      See results
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ),
      },
    ],
    [
      answers,
      countries,
      subjects,
      acceptTerms,
      isInvalidEmail,
      isInvalidName,
      isInvalidPhone,
      prioritiesOptions,
      currentQuestion,
      askEntireQuestionary,
      answersPercentage,
      skipQuestion,
    ]
  );

  return (
    <div className={styles.container}>
      {isLoading ? (
        <div className={styles.profileContainer}>
          <RobotLoader />
        </div>
      ) : (
        <>
          {askEmail ? (
            <div>
              {!isMobile && (
                <div className={styles.closeModal}>
                  <CloseIcon className={styles.clsoe} onClick={() => navigate(-1)} />
                </div>
              )}
              <div className={styles.profileContainer}>
                <div className={styles.profileHeader}>
                  {isMobile && (
                    <div className={styles.headerControl}>
                      <div className={`mb-3 ${styles.prevPageIcon}`} onClick={onPrevQuestion}>
                        <i className="bi bi-chevron-left"></i>
                      </div>
                    </div>
                  )}
                  <div className={styles.headerRobot}>
                    <RobotIcon />
                  </div>
                  {/* <div className={styles.headerContent}>
              <div className={styles.headerEmailWarning}>
                Enter your email to load your results
              </div>
            </div> */}
                </div>
                <div className={styles.profileProgressBar}>
                  <div className={styles.barProgress} style={{ width: '100%' }}></div>
                </div>

                <div className={styles.quetionsContainer}>
                  <div className={styles.question}>
                    <Text
                      size={isMobile ? 20 : 24}
                      weight={700}
                      color="primary"
                      inlineStyle={{ marginTop: '50px' }}
                    >
                      Enter your email to load your results
                    </Text>
                    <div className={styles.questionContent}>
                      <input
                        className={`form-control ${isInvalidEmail && styles.warningInput}`}
                        onChange={(e) => setInputOption('email', e.target.value)}
                        placeholder="My email"
                      />
                      {isInvalidEmail && (
                        <label className={styles.warningLabel}>{warningAdvice}</label>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.questionsControl}>
                  <button className={styles.questionsNext} onClick={validateProfile}>
                    See Results
                  </button>
                  <div className={styles.questionsSkip} onClick={cleanQuestions}>
                    Answer questions again
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {!isMobile && (
                <div className={styles.closeModal}>
                  <CloseIcon className={styles.clsoe} onClick={() => navigate(-1)} />
                </div>
              )}
              <div className={styles.profileContainer}>
                {questions[currentQuestion].noHeader ? (
                  questions[currentQuestion].content
                ) : (
                  <div>
                    <div className={styles.profileHeader}>
                      <div className={styles.headerControl}>
                        <div className={styles.prevPageIcon} onClick={onPrevQuestion}>
                          <i className="bi bi-chevron-left"></i>
                        </div>
                        <div className={styles.headerInfo}>
                          <span>
                            <i className="bi bi-clock"></i> 3 mins
                          </span>
                          <span>
                            <i className="bi bi-card-list"></i> 6 questions
                          </span>
                        </div>
                      </div>
                      <div className={styles.headerRobot}>
                        <RobotIcon />
                      </div>
                      <div className={styles.headerContent}>
                        <div className={styles.headerWarning}>
                          Skipping questions will influence the accuracy rate
                        </div>
                        <div className={styles.headerProgress}>
                          Accuracy of Preference rate...{' '}
                          {isMobile ? (
                            <div className={styles.progressPercent}>{answersPercentage}%</div>
                          ) : (
                            <div className={styles.progressPercent}>
                              <CircularProgressbarWithChildren
                                value={answersPercentage}
                                strokeWidth={10}
                                styles={buildStyles({
                                  pathColor: '#2071F1',
                                  trailColor: 'white',
                                  backgroundColor: '#3e98c7',
                                })}
                              >
                                <div className={styles.graphText}>{answersPercentage}%</div>
                              </CircularProgressbarWithChildren>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={styles.profileProgressBar}>
                      <div
                        className={styles.barProgress}
                        style={{ width: `${currentProgress}%` }}
                      ></div>
                    </div>

                    <div className={styles.quetionsContainer}>
                      <div className={styles.question}>
                        {isMobile ? (
                          <>
                            <Text
                              size={20}
                              weight={700}
                              color="primary"
                              inlineStyle={{ marginRight: '5px' }}
                            >
                              {questions[currentQuestion].title}
                            </Text>
                            <Text size={14} color="secondary" inline>
                              {questions[currentQuestion].comment}
                            </Text>
                          </>
                        ) : (
                          <>
                            <div className={styles.questionTitle}>
                              {questions[currentQuestion].title}
                            </div>
                            <div className={styles.questionComment}>
                              {questions[currentQuestion].comment}
                            </div>
                          </>
                        )}
                        {questions[currentQuestion].content}
                      </div>
                    </div>
                    <div className={styles.questionsControl}>
                      <button
                        className={`${styles.questionsNext} ${
                          !(
                            currentQuestion === 5 &&
                            answers.score_technical_exam !== undefined &&
                            answers.score_technical_exam === 0
                          )
                            ? ''
                            : styles.disabledButton
                        }`}
                        onClick={() => onNextQuestion(false)}
                        disabled={
                          currentQuestion === 5 &&
                          answers.score_technical_exam !== undefined &&
                          answers.score_technical_exam === 0
                        }
                      >
                        {questions[currentQuestion].lastQuestion ? 'Next' : 'Next Question'}
                      </button>
                      <div className={styles.questionsSkip} onClick={() => onNextQuestion(true)}>
                        I am not sure, skip
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Profile;
